import type { ReactNode } from 'react';
import type { ScheduleItemView } from '@obraplan/core/application/contracts/schedule-items';
import { BusinessDate } from '@obraplan/core/domain/date/business-date';
import { cn } from '@/lib/cn';
import { formatBusinessDate } from '@/lib/format';
import {
  barOn,
  businessDaysBetween,
  describeBar,
  describeSummaryBar,
  formatDecimal,
  formatShortRange,
  isStalled,
  labeledMonths,
  positionOn,
  progressOf,
  weekTicks,
  type BarSummary,
  type Timeline,
} from './schedule-items-model';

/**
 * O Gantt do cronograma por mão de obra — SVG desenhado aqui, sem biblioteca e sem CDN
 * (RN-138), como os gráficos da Fase 10. Redesenhado em 21/10/2026: o eixo do tempo é o
 * protagonista da tela, e o que a barra não diz sozinha fica ao lado dela, em texto.
 *
 * Cada linha leva a sua barra, e todas usam a mesma escala em porcentagem (`Timeline`): quem
 * lê de cima para baixo compara as barras de pavimentos diferentes na mesma régua.
 *
 * O que se desenha:
 * - **Previsto:** a barra, na cor da disciplina, clara e com contorno.
 * - **Realizado:** a parte cheia, sólida, do previsto. Verde quando chega a 100%.
 * - **Linha de base:** a faixa fina por baixo — só quando difere do previsto; igual, ela não
 *   acrescenta nada. O trecho do previsto que passa dela é o **atraso**, em âmbar.
 * - **Sem avanço no prazo:** o item que já devia ter terminado e está em 0% ganha o contorno
 *   e o ponto âmbar ao lado do rótulo.
 * - **Hoje:** a linha vertical azul, com a data escrita no eixo.
 *
 * Ao lado de cada barra, o rótulo: a faixa de datas e os dias úteis. O resto (quantidade, HH,
 * equipe) está no detalhe da linha, não na coluna.
 *
 * Cor nunca é o único sinal (PLANO_GERAL §4.4): a espessura e a posição distinguem as faixas,
 * a legenda as nomeia e cada barra tem o texto completo em `aria-label` e `<title>`.
 */

export const ROW_HEIGHT = 36;
export const SUMMARY_HEIGHT = 34;
export const AXIS_HEIGHT = 60;

/** A cor de quem não tem disciplina colorida: a da marca. */
export const DEFAULT_BAR_COLOR = 'var(--color-brand)';

const DONE = 'var(--color-state-ok)';
const BASELINE = 'color-mix(in oklab, var(--color-content) 55%, transparent)';
const LATE = 'color-mix(in oklab, var(--color-state-warn) 70%, transparent)';
const STALLED = 'var(--color-state-warn)';
const TODAY = 'var(--color-brand)';
const GRID = 'var(--color-border-subtle)';
const SUMMARY_FILL = 'color-mix(in oklab, var(--color-content-muted) 70%, transparent)';
const SUMMARY_DONE = 'var(--color-content)';
const LABEL = 'var(--color-content-muted)';
const CONDENSED = 'var(--font-condensed)';

function plannedFill(color: string): string {
  return `color-mix(in oklab, ${color} 30%, var(--color-surface))`;
}
function plannedStroke(color: string): string {
  return `color-mix(in oklab, ${color} 80%, transparent)`;
}

function pct(value: number): string {
  return `${String(Math.round(value * 1000) / 1000)}%`;
}

/** O dia seguinte, ou o mesmo se a data não for válida. */
function nextDay(date: string): string {
  const parsed = BusinessDate.parse(date);
  return parsed.ok ? parsed.value.addDays(1).toString() : date;
}

/** Hoje cai dentro da escala? Então devolve a posição, de 0 a 100. */
function todayOn(timeline: Timeline, today: string | undefined): number | undefined {
  if (today === undefined || today < timeline.start || today > timeline.end) return undefined;
  return positionOn(timeline, today);
}

/** A grade dos meses e a linha de hoje, iguais em toda linha. */
function Grid({
  timeline,
  today,
  height,
}: {
  readonly timeline: Timeline;
  readonly today: string | undefined;
  readonly height: number;
}) {
  const todayAt = todayOn(timeline, today);
  return (
    <>
      {labeledMonths(timeline).map((month) => (
        <line
          key={month.key}
          data-part="grid"
          x1={pct(month.pct)}
          x2={pct(month.pct)}
          y1={0}
          y2={height}
          style={{ stroke: GRID }}
          strokeWidth={1}
        />
      ))}
      {todayAt !== undefined && (
        <line
          data-part="today"
          x1={pct(todayAt)}
          x2={pct(todayAt)}
          y1={0}
          y2={height}
          style={{ stroke: TODAY, opacity: 0.55 }}
          strokeWidth={1}
        />
      )}
    </>
  );
}

/** O trecho do previsto depois do último dia da linha de base — o atraso. */
function lateWindow(data: {
  readonly plannedStart: string;
  readonly plannedEnd: string;
  readonly baselineEnd: string;
}): { readonly start: string; readonly end: string } | undefined {
  if (data.plannedEnd <= data.baselineEnd) return undefined;
  const start =
    data.plannedStart > data.baselineEnd ? data.plannedStart : nextDay(data.baselineEnd);
  return { start, end: data.plannedEnd };
}

/** A barra de um item: previsto, realizado, linha de base quando difere, e o rótulo ao lado. */
export function GanttBar({
  item,
  timeline,
  today,
  color = DEFAULT_BAR_COLOR,
}: {
  readonly item: ScheduleItemView;
  readonly timeline: Timeline;
  readonly today?: string | undefined;
  /** A cor da disciplina do item. */
  readonly color?: string;
}) {
  const planned = barOn(timeline, item.plannedStart, item.plannedEnd);
  const baselineDiffers =
    item.baselineStart !== item.plannedStart || item.baselineEnd !== item.plannedEnd;
  const baseline = barOn(timeline, item.baselineStart, item.baselineEnd);
  const done = Math.min(100, Math.max(0, progressOf(item)));
  const late = lateWindow(item);
  const lateBar = late === undefined ? undefined : barOn(timeline, late.start, late.end);
  const stalled = isStalled(item, today);
  const end = planned.left + planned.width;
  // O rótulo cabe à direita até uns 78% da régua; depois, vai para a esquerda da barra.
  const labelLeft = end > 78;
  const days = businessDaysBetween(item.plannedStart, item.plannedEnd);
  const label = `${formatShortRange(item.plannedStart, item.plannedEnd)} · ${String(days)} ${days === 1 ? 'dia' : 'dias'}`;
  const description = `${describeBar(item)}${stalled ? ' Já devia ter terminado e não tem avanço.' : ''}`;

  const top = 10;
  const thick = 16;

  return (
    <svg
      role="img"
      aria-label={description}
      width="100%"
      height={ROW_HEIGHT}
      className="block overflow-visible"
      data-testid="gantt-bar"
      data-stalled={stalled ? 'true' : undefined}
    >
      <title>{description}</title>
      <Grid timeline={timeline} today={today} height={ROW_HEIGHT} />
      {baselineDiffers && (
        <rect
          data-part="baseline"
          x={pct(baseline.left)}
          y={top + thick + 3}
          width={pct(baseline.width)}
          height={2}
          rx={1}
          style={{ fill: BASELINE }}
        />
      )}
      <rect
        data-part="planned"
        x={pct(planned.left)}
        y={top}
        width={pct(planned.width)}
        height={thick}
        rx={3}
        style={{
          fill: plannedFill(color),
          stroke: stalled ? STALLED : plannedStroke(color),
        }}
        strokeWidth={stalled ? 1.5 : 1}
      />
      {lateBar !== undefined && (
        <rect
          data-part="late"
          x={pct(lateBar.left)}
          y={top}
          width={pct(lateBar.width)}
          height={thick}
          rx={3}
          style={{ fill: LATE }}
        />
      )}
      {done > 0 && (
        <rect
          data-part="progress"
          x={pct(planned.left)}
          y={top}
          width={pct((planned.width * done) / 100)}
          height={thick}
          rx={3}
          style={{ fill: done >= 100 ? DONE : color }}
        />
      )}
      <svg
        x={pct(labelLeft ? planned.left : end)}
        y={0}
        overflow="visible"
        data-part="label"
        aria-hidden="true"
      >
        <text
          x={labelLeft ? -8 : 8}
          y={top + thick - 4}
          textAnchor={labelLeft ? 'end' : 'start'}
          fontSize={12}
          style={{ fill: LABEL, fontFamily: CONDENSED, fontVariantNumeric: 'tabular-nums' }}
        >
          {stalled && (
            <tspan data-part="stalled" style={{ fill: STALLED }}>
              ●{' '}
            </tspan>
          )}
          {label}
        </text>
      </svg>
    </svg>
  );
}

/**
 * A faixa de um grupo — o pavimento, a torre: a janela de todos os itens dele, fina, com o
 * avanço pesado pelo HH escrito na ponta; a linha de base só quando difere.
 */
export function GanttSummaryBar({
  name,
  summary,
  timeline,
  today,
}: {
  readonly name: string;
  readonly summary: BarSummary;
  readonly timeline: Timeline;
  readonly today?: string | undefined;
}) {
  const planned = barOn(timeline, summary.plannedStart, summary.plannedEnd);
  const baselineDiffers =
    summary.baselineStart !== summary.plannedStart || summary.baselineEnd !== summary.plannedEnd;
  const baseline = barOn(timeline, summary.baselineStart, summary.baselineEnd);
  const done = Math.min(100, Math.max(0, summary.progressPct));
  const late = lateWindow(summary);
  const lateBar = late === undefined ? undefined : barOn(timeline, late.start, late.end);
  const end = planned.left + planned.width;
  const percentInside = end > 88;
  const description = describeSummaryBar(name, summary);
  const y = 15;
  const thin = 4;

  return (
    <svg
      role="img"
      aria-label={description}
      width="100%"
      height={SUMMARY_HEIGHT}
      className="block overflow-visible"
      data-testid="gantt-summary"
    >
      <title>{description}</title>
      <Grid timeline={timeline} today={today} height={SUMMARY_HEIGHT} />
      {baselineDiffers && (
        <rect
          data-part="baseline"
          x={pct(baseline.left)}
          y={y + thin + 3}
          width={pct(baseline.width)}
          height={2}
          rx={1}
          style={{ fill: BASELINE }}
        />
      )}
      <rect
        data-part="planned"
        x={pct(planned.left)}
        y={y}
        width={pct(planned.width)}
        height={thin}
        rx={2}
        style={{ fill: SUMMARY_FILL }}
      />
      {lateBar !== undefined && (
        <rect
          data-part="late"
          x={pct(lateBar.left)}
          y={y}
          width={pct(lateBar.width)}
          height={thin}
          rx={2}
          style={{ fill: LATE }}
        />
      )}
      {done > 0 && (
        <rect
          data-part="progress"
          x={pct(planned.left)}
          y={y}
          width={pct((planned.width * done) / 100)}
          height={thin}
          rx={2}
          style={{ fill: done >= 100 ? DONE : SUMMARY_DONE }}
        />
      )}
      <text
        data-part="percent"
        x={pct(end)}
        dx={percentInside ? -6 : 8}
        y={y + thin + 1}
        textAnchor={percentInside ? 'end' : 'start'}
        fontSize={11}
        style={{
          fill: LABEL,
          fontFamily: CONDENSED,
          fontWeight: 500,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatDecimal(done, 1)}%
      </text>
    </svg>
  );
}

/**
 * O eixo do tempo, para o cabeçalho: os meses, a régua das semanas por baixo e o hoje com a
 * data. A legenda, quando vem, senta no canto direito do eixo.
 */
export function GanttAxis({
  timeline,
  today,
  legend,
}: {
  readonly timeline: Timeline;
  readonly today?: string | undefined;
  readonly legend?: ReactNode;
}) {
  const labeled = new Set(labeledMonths(timeline).map((month) => month.key));
  const weeks = weekTicks(timeline);
  const description = `Escala de ${formatBusinessDate(timeline.start)} a ${formatBusinessDate(timeline.end)}, em meses${weeks.length > 0 ? ' e semanas' : ''}.`;
  const todayAt = todayOn(timeline, today);
  const todayLabel = today === undefined ? '' : `hoje · ${formatShortRange(today, today)}`;

  return (
    <div className="relative">
      <svg
        role="img"
        aria-label={description}
        width="100%"
        height={AXIS_HEIGHT}
        className="block overflow-visible"
        data-testid="gantt-axis"
        style={{ fontFamily: CONDENSED, textTransform: 'none', letterSpacing: 'normal' }}
      >
        {timeline.months.map((month) => (
          <g key={month.key}>
            <line
              x1={pct(month.pct)}
              x2={pct(month.pct)}
              y1={labeled.has(month.key) ? 24 : 42}
              y2={AXIS_HEIGHT}
              style={{ stroke: GRID }}
              strokeWidth={1}
            />
            {labeled.has(month.key) && (
              <text
                x={pct(month.pct)}
                y={37}
                dx={6}
                fontSize={13}
                style={{ fill: 'var(--color-content)', fontWeight: 600 }}
              >
                {month.label}
              </text>
            )}
          </g>
        ))}
        {weeks.map((week) => (
          <g key={week.key} data-part="week">
            <line
              x1={pct(week.pct)}
              x2={pct(week.pct)}
              y1={46}
              y2={AXIS_HEIGHT}
              style={{ stroke: GRID }}
              strokeWidth={1}
            />
            <text
              x={pct(week.pct)}
              y={57}
              dx={3}
              fontSize={10}
              style={{ fill: LABEL, fontVariantNumeric: 'tabular-nums' }}
            >
              {week.day}
            </text>
          </g>
        ))}
        {todayAt !== undefined && (
          <svg x={pct(todayAt)} y={0} overflow="visible" data-part="today">
            <line x1={0} x2={0} y1={2} y2={AXIS_HEIGHT} style={{ stroke: TODAY }} strokeWidth={2} />
            <rect x={-34} y={0} width={68} height={16} rx={4} style={{ fill: TODAY }} />
            <text
              x={0}
              y={11.5}
              textAnchor="middle"
              fontSize={10.5}
              style={{ fill: 'var(--color-brand-contrast)', fontWeight: 600 }}
            >
              {todayLabel}
            </text>
          </svg>
        )}
      </svg>
      {legend !== undefined && (
        <div className="absolute top-0 right-0 hidden bg-(--color-surface-raised) pb-1 pl-3 lg:block">
          {legend}
        </div>
      )}
    </div>
  );
}

/** O que cada faixa quer dizer — em palavras, ao lado do desenho. */
export function GanttLegend({ className }: { readonly className?: string }) {
  return (
    <ul
      className={cn(
        'font-condensed flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[11px] font-normal tracking-normal normal-case text-(--color-content-muted)',
        className,
      )}
      aria-label="Legenda do Gantt"
    >
      <li className="flex items-center gap-1.5">
        <svg width="18" height="10" aria-hidden="true">
          <rect
            x="0.5"
            y="0.5"
            width="17"
            height="9"
            rx="2"
            style={{
              fill: plannedFill(DEFAULT_BAR_COLOR),
              stroke: plannedStroke(DEFAULT_BAR_COLOR),
            }}
          />
        </svg>
        previsto
      </li>
      <li className="flex items-center gap-1.5">
        <svg width="18" height="10" aria-hidden="true">
          <rect width="18" height="10" rx="2" style={{ fill: DEFAULT_BAR_COLOR }} />
        </svg>
        realizado
      </li>
      <li className="flex items-center gap-1.5">
        <svg width="18" height="10" aria-hidden="true">
          <rect x="7" width="11" height="6" rx="2" style={{ fill: LATE }} />
          <rect y="8" width="12" height="2" rx="1" style={{ fill: BASELINE }} />
        </svg>
        linha de base e atraso, quando diferem
      </li>
      <li className="flex items-center gap-1.5">
        <svg width="10" height="10" aria-hidden="true">
          <circle cx="5" cy="5" r="4" style={{ fill: STALLED }} />
        </svg>
        sem avanço no prazo
      </li>
    </ul>
  );
}
